import React, { useState, useEffect, useRef } from 'react';
import { Home, Search, ArrowUpCircle, Youtube, Settings, DollarSign, LogOut, BookOpen, Cpu, MessageSquare, Moon, Sun, Menu, X } from 'react-feather';
import { useSettingsManager } from '../Configuration/SettingsManager';
import NextStockTitleMark from '../components/NextStockTitleMark';
import firebase from '../Firebase/firebaseConnection';
import './HeaderCSS.css';
import { applyAppearance, loadAppearance, saveAppearance, effective, timezone } from '../Resources/appearance';

import { useTranslation } from "react-i18next";

export default function Header({ setPage }) {

  const { t, i18n } = useTranslation();

  const { user, getUserFromStorage, handleSettingsPress } = useSettingsManager();
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);
  const navRef = useRef(null);
  const menuButtonRef = useRef(null);
  const themeRef = useRef(null);
  const [theme, setTheme] = useState(() => { try { return localStorage.getItem('nextstock-theme') === 'dark' ? 'dark' : (localStorage.getItem('nextstock-appearance-mode') ? 'light' : effective('auto')); } catch { return 'light'; } });
  const [appearanceMode, setAppearanceMode] = useState(() => localStorage.getItem('nextstock-appearance-mode') || 'auto');
  const [autoNotice, setAutoNotice] = useState(false);
  const [themeSaving, setThemeSaving] = useState(null);
  const [themeSaveError, setThemeSaveError] = useState(false);
  const [showThemeHint, setShowThemeHint] = useState(() => {
    try {
      const dismissedAt = Number(localStorage.getItem('nextstock-theme-hint-dismissed-at'));
      return !Number.isFinite(dismissedAt) || dismissedAt <= 0 || Date.now() - dismissedAt >= 30 * 24 * 60 * 60 * 1000;
    } catch { return true; }
  });
  useEffect(() => {
    const onOutside = event => {
      if (mobileOpen && !navRef.current?.contains(event.target) && !menuButtonRef.current?.contains(event.target)) setMobileOpen(false);
      if (showThemeHint && autoNotice && !themeRef.current?.contains(event.target)) {
        // Dismiss only the informational popup, never change the appearance preference.
        setShowThemeHint(false);
      }
    };
    document.addEventListener('pointerdown', onOutside);
    return () => document.removeEventListener('pointerdown', onOutside);
  }, [mobileOpen, showThemeHint, autoNotice]);
  const dismissThemeHint = () => {
    try { localStorage.setItem('nextstock-theme-hint-dismissed-at', String(Date.now())); } catch {}
    setShowThemeHint(false);
  };
  const activePage = new URLSearchParams(window.location.search).get('page') || 'Home';
  useEffect(() => {
    const update = (event) => {
      const mode = event?.detail?.mode || localStorage.getItem('nextstock-appearance-mode') || 'auto';
      setAppearanceMode(mode);
      setTheme(effective(mode, localStorage.getItem('nextstock-appearance-timezone') || timezone()));
    };
    window.addEventListener('nextstock-appearance-changed', update);
    const unsubscribe = firebase.auth().onAuthStateChanged(user => { if (user) loadAppearance().catch(() => {}); });
    const interval = setInterval(() => {
      if ((localStorage.getItem('nextstock-appearance-mode') || 'auto') === 'auto') {
        applyAppearance('auto', localStorage.getItem('nextstock-appearance-timezone') || timezone());
      }
    }, 60000);
    if (!localStorage.getItem('nextstock-appearance-mode')) applyAppearance('auto');
    update();
    return () => { window.removeEventListener('nextstock-appearance-changed', update); unsubscribe(); clearInterval(interval); };
  }, []);
  const switchFromAuto = async () => {
    if (themeSaving) return;
    const next = theme === 'light' ? 'dark' : 'light';
    setThemeSaveError(false);
    setThemeSaving(next);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      await saveAppearance(next, { signal: controller.signal });
      setAutoNotice(false);
      dismissThemeHint();
    } catch {
      setThemeSaveError(true);
    } finally {
      clearTimeout(timeout);
      setThemeSaving(null);
    }
  };
  const toggleTheme = async () => {
    if (appearanceMode === 'auto') { setAutoNotice(true); setShowThemeHint(true); return; }
    if (themeSaving) return;
    const next = theme === 'light' ? 'dark' : 'light';
    setThemeSaveError(false);
    setThemeSaving(next);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try { await saveAppearance(next, { signal: controller.signal }); }
    catch { setThemeSaveError(true); }
    finally { clearTimeout(timeout); setThemeSaving(null); }
  };

  useEffect(() => {
    getUserFromStorage();
  }, []);



  const handleLogout = () => {
    const baseUrl = window.location.origin + window.location.pathname.split("?")[0];
    localStorage.clear();
    window.open(`${baseUrl}?page=Signin`, "_self");
  };

  function handleMenuNavigation(pageName) {
    return () => {
      setMobileOpen(false);
      setPage(pageName);
      const baseUrl = window.location.origin + window.location.pathname.split("?")[0];
      window.history.replaceState(null, "", `${baseUrl}?page=${pageName}`);
      window.scrollTo(0, 0);
    };
  }

  const openAppearanceSettings = () => {
    dismissThemeHint();
    setAutoNotice(false);
    handleMenuNavigation('Settings')();
    window.requestAnimationFrame(() => {
      document.querySelector('.ns-appearance-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const links = [
    ['Home', Home, 'home'], ['Searchp', Search, 'search'], ['Top10', ArrowUpCircle, 'top10'],
    ['Portfolio', BookOpen, 'portfolio'], ['Youtube', Youtube, 'channel'],
    ['ai-chat', MessageSquare, 'aiChat'], ['subscription', DollarSign, 'subscription'], ['Settings', Settings, 'settings']
  ];
  return (
    <header className="header" ref={menuRef}>
      <div className="header-shell">
        <button type="button" className="ns-logo-button" onClick={handleMenuNavigation('Home')} aria-label="Next Stock home">
          <NextStockTitleMark className="logo-next-stock" />
        </button>
        <nav ref={navRef} className={`ns-navigation ${mobileOpen ? 'is-open' : ''}`} aria-label="Main navigation">
          {links.map(([page, Icon, key]) => (
            <button key={page} type="button" className={`ns-nav-link ${activePage === page ? 'is-active' : ''}`}
              aria-current={activePage === page ? 'page' : undefined}
              onClick={page === 'subscription' ? () => { setMobileOpen(false); handleSettingsPress(); } : handleMenuNavigation(page)}>
              <Icon size={18} strokeWidth={1.9} /><span>{t(`header.${key}`)}</span>
            </button>
          ))}
        </nav>
        <div className="header-right">
          <div className="ns-language" role="group" aria-label="Language">
            <button type="button" className={i18n.language?.startsWith('en') ? 'is-active' : ''} onClick={() => i18n.changeLanguage('en')} aria-pressed={i18n.language?.startsWith('en')}>EN</button>
            <button type="button" className={i18n.language?.startsWith('fr') ? 'is-active' : ''} onClick={() => i18n.changeLanguage('fr')} aria-pressed={i18n.language?.startsWith('fr')}>FR</button>
          </div>
          <div className="ns-theme-control" ref={themeRef}>
            <button type="button" className={`ns-theme-toggle ${themeSaving ? `is-saving is-saving-${themeSaving}` : ""}`} onClick={toggleTheme} disabled={!!themeSaving} aria-busy={!!themeSaving}
              aria-label={theme === 'light' ? 'Enable dark mode' : 'Enable light mode'} title={theme === 'light' ? 'Dark mode' : 'Light mode'}>
              {themeSaving ? <span className="ns-theme-loading-orbit" aria-hidden="true"><span className="ns-theme-loading-core">{themeSaving === 'dark' ? <Moon size={12} /> : <Sun size={12} />}</span></span> : (theme === 'light' ? <Moon size={18} /> : <Sun size={18} />)}
            </button>
            {themeSaveError && <span className="ns-theme-save-error" role="alert" onClick={() => setThemeSaveError(false)}>Could not save theme. Please try again.</span>}
            {showThemeHint && (
              <div className="ns-theme-hint" role="status">
                <div className="ns-theme-hint-heading"><span className="ns-theme-hint-icon"><Sun size={17} /></span>{autoNotice ? 'Auto Mode is active' : (i18n.language?.startsWith('fr') ? 'Choisissez votre ambiance' : 'Make it yours')}</div>
                <p>{autoNotice ? 'Your theme follows the time of day. To keep one mode, change Appearance in ' : (i18n.language?.startsWith('fr') ? 'Passez du mode clair au mode sombre ici. Pour changer automatiquement selon l’heure, activez le mode Auto dans ' : 'Switch between Light and Dark here. For automatic themes, enable Auto Mode in ')}<button type="button" className="ns-theme-settings-link" onClick={openAppearanceSettings}><Settings size={10} aria-hidden="true" />{i18n.language?.startsWith('fr') ? 'Paramètres' : 'Settings'}</button> → {i18n.language?.startsWith('fr') ? 'Apparence' : 'Appearance'}.</p>
                {autoNotice && appearanceMode === 'auto' && (
                  <button type="button" className={`ns-theme-auto-override ${themeSaving ? 'is-loading' : ''}`} onClick={switchFromAuto} disabled={!!themeSaving} aria-busy={!!themeSaving}>
                    <span className="ns-theme-auto-override-glow" aria-hidden="true" />
                    <span className="ns-theme-auto-override-label">
                      {themeSaving ? <span className="ns-theme-auto-override-spinner" aria-hidden="true" /> : (theme === 'light' ? <Moon size={17} /> : <Sun size={17} />)}
                      {themeSaving ? 'Saving your preference…' : `Switch to ${theme === 'light' ? 'Dark' : 'Light'} & keep it on`}
                    </span>
                    {!themeSaving && <span className="ns-theme-auto-override-sub">Turn off Auto Mode</span>}
                  </button>
                )}
                <button type="button" className="ns-theme-hint-button" onClick={dismissThemeHint}>{autoNotice && appearanceMode === 'auto' ? (i18n.language?.startsWith('fr') ? 'Garder le mode Auto' : 'Keep Auto Mode') : (i18n.language?.startsWith('fr') ? 'Compris !' : 'Got it!')}</button>
              </div>
            )}
          </div>
          <span className="ns-user-email" title={user?.email || ''}>{user?.email || ''}</span>
          <button type="button" className="ns-logout" onClick={handleLogout} title={t('header.logout')}>
            <LogOut size={17} /><span>{t('header.logout')}</span>
          </button>
          <button ref={menuButtonRef} type="button" className="ns-mobile-menu" onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileOpen}>
            {mobileOpen ? <X size={21} /> : <Menu size={21} />}
          </button>
        </div>
      </div>
    </header>
  );
}
